/**
 * NUOVA PRENOTAZIONE — dove finisce ogni tipo (23-10-2026).
 *
 * Fino a oggi «Prenotazioni» scriveva tutto con `POST /api/bookings`: una riga `VehicleBooking`
 * con `requestType` 1-7 e lo stato vecchio (`ManagerApproval`), che il flusso nuovo non legge e
 * non scrive. Le richieste nascevano quindi fuori da ogni coda vera: il responsabile le vedeva
 * solo nella coda vecchia, la segreteria in nessuna.
 *
 * Ora si scrive come Lares (`trip-booking.js`), sulle tre risorse che hanno un flusso:
 *
 * | Cosa si prenota | Risorsa |
 * |---|---|
 * | auto aziendale | `vehicle-bookings` (stato `requested`, il mezzo lo assegna la segreteria) |
 * | sala riunioni | `meeting-room-bookings` (`confirmed` subito se la sala non chiede conferma) |
 * | treno, aereo, traghetto, noleggio, transfer, parcheggio, pernottamento, altro | `travel-bookings` |
 *
 * Qui sta solo la decisione e la forma del corpo: niente chiamate, così l'instradamento si prova
 * da solo (`newBookingRouting.test.ts`).
 */
import dayjs from 'dayjs';
import { TRAVEL_TYPES } from '@/features/travel/travelApi';

export type TravelType = (typeof TRAVEL_TYPES)[number];
/** La scelta fatta nel form: l'auto, la sala, o uno dei tipi di trasferta. */
export type BookingChoice = 'vehicle' | 'room' | TravelType;
export type BookingTarget = 'vehicle-bookings' | 'meeting-room-bookings' | 'travel-bookings';

export function targetOf(choice: BookingChoice): BookingTarget {
  if (choice === 'vehicle') return 'vehicle-bookings';
  if (choice === 'room') return 'meeting-room-bookings';
  return 'travel-bookings';
}

export function isTravelChoice(choice: BookingChoice): choice is TravelType {
  return (TRAVEL_TYPES as readonly string[]).includes(choice);
}

/**
 * `?type=` di `/bookings/new`. Accetta i nomi nuovi (`vehicle`, `room`, `train`, `flight`…) e
 * quelli di prima — i numeri 1-7 e `plane`/`rental`/`ship` — perché i collegamenti già
 * salvati continuino ad aprire il form sul tipo giusto.
 */
const LEGACY_TYPE: Record<string, BookingChoice> = {
  1: 'vehicle', 2: 'room', 3: 'train', 4: 'flight', 5: 'hotel', 6: 'car-rental-short', 7: 'ferry',
  car: 'vehicle', 'meeting-room': 'room', plane: 'flight', rental: 'car-rental-short', ship: 'ferry',
};
export function parseBookingChoice(raw: unknown): BookingChoice | undefined {
  if (raw == null || raw === '') return undefined;
  const key = String(raw);
  if (key === 'vehicle' || key === 'room' || (TRAVEL_TYPES as readonly string[]).includes(key)) return key as BookingChoice;
  return LEGACY_TYPE[key];
}

/** Quello che il form raccoglie, uguale per desktop e telefono. Date e ore **locali**. */
export interface NewBookingForm {
  choice: BookingChoice;
  /** `YYYY-MM-DDTHH:mm:ss`, ora del muro di chi prenota. */
  start: string;
  end: string;
  destination: string;
  purpose: string;
  projectId?: string | null;
  roomId?: string | null;
  /** Colleghi coinvolti oltre a chi prenota (id di `erp-employees`). */
  peopleIds: string[];
}

/**
 * Cosa manca per poter inviare, in parole: `null` se il form è completo. Le regole sono quelle
 * di Lares (`trip-booking.js` §validazione) più quello che il sistema rifiuta: motivo
 * obbligatorio sulle trasferte (422), fine non prima dell'inizio, una sala per la sala.
 */
export function missingFor(f: NewBookingForm): string | null {
  if (!f.start || !f.end) return 'Indica inizio e fine.';
  if (!dayjs(f.end).isAfter(dayjs(f.start))) return 'La fine deve venire dopo l’inizio.';
  if (f.choice === 'room') {
    if (!f.roomId) return 'Scegli una sala.';
  } else if (!f.destination.trim()) {
    return 'Indica la destinazione.';
  }
  if (!f.purpose.trim()) return 'Scrivi il motivo della prenotazione.';
  return null;
}

/** Un istante locale come lo vuole il sistema: UTC con la `Z`, come fa Lares (`date-utc`). */
const utc = (local: string) => dayjs(local).toISOString();

export type CreatePlan =
  | { target: 'vehicle-bookings'; attributes: Record<string, unknown>; projectId: string | null }
  | { target: 'meeting-room-bookings'; attributes: Record<string, unknown>; roomId: string }
  | { target: 'travel-bookings'; attributes: Record<string, unknown>; projectId: string | null };

/**
 * Il corpo della create, per risorsa. Si scrive qui e non nei due form perché è il punto in cui
 * desktop e telefono divergevano senza che nessuno se ne accorgesse.
 */
export function planCreate(f: NewBookingForm): CreatePlan {
  const purpose = f.purpose.trim();
  const projectId = f.projectId || null;
  if (f.choice === 'vehicle') {
    return {
      target: 'vehicle-bookings',
      projectId,
      attributes: {
        fromDate: utc(f.start),
        toDate: utc(f.end),
        purpose,
        destinationLocation: f.destination.trim(),
        priority: 'normal',
      },
    };
  }
  if (f.choice === 'room') {
    return {
      target: 'meeting-room-bookings',
      roomId: f.roomId ?? '',
      attributes: {
        fromDateTime: utc(f.start),
        toDateTime: utc(f.end),
        purpose,
        // Chi prenota più i colleghi invitati: il sistema lo confronta con la capienza.
        attendeeCount: 1 + f.peopleIds.length,
        layoutType: 'boardroom',
      },
    };
  }
  const attributes: Record<string, unknown> = {
    travelType: f.choice,
    // `DateOnly` sul sistema: la sola data, nel giorno di chi prenota.
    fromDate: dayjs(f.start).format('YYYY-MM-DD'),
    toDate: dayjs(f.end).format('YYYY-MM-DD'),
    purpose,
    destination: f.destination.trim(),
    priority: 'normal',
    payerType: 'company-prepaid',
    currency: 'EUR',
    // Non nullo sul sistema: l'importo lo scrive la segreteria quando conferma.
    amount: 0,
  };
  if (f.choice === 'hotel') {
    // Il pernottamento ha le ore vere nell'arrivo e nella partenza.
    attributes.checkInAt = utc(f.start);
    attributes.checkOutAt = utc(f.end);
  }
  return { target: 'travel-bookings', projectId, attributes };
}
